import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  const fetchCurrentUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get(`${API}/admin/me`);
      setUser(response.data.user);
      setPermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Token is invalid, clear it
      localStorage.removeItem('adminToken');
      setToken(null);
      setUser(null);
      setPermissions([]);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('adminToken', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      // Fetch permissions
      try {
        const meResponse = await axios.get(`${API}/admin/me`);
        setPermissions(meResponse.data.permissions || []);
      } catch (e) {
        console.error('Failed to fetch permissions:', e);
      }
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setToken(null);
    setUser(null);
    setPermissions([]);
    localStorage.removeItem('adminToken');
    delete axios.defaults.headers.common['Authorization'];
  };

  const hasPermission = (permission) => {
    if (!permissions || permissions.length === 0) return false;
    if (permissions.includes('all')) return true;
    if (permissions.includes(permission)) return true;
    
    // Check wildcard (e.g., jobs.* matches jobs.view)
    const base = permission.split('.')[0];
    if (permissions.includes(`${base}.*`)) return true;
    
    return false;
  };

  const canAccess = (section) => {
    const sectionPermissions = {
      jobs: ['jobs.view', 'jobs.edit', 'jobs.create', 'jobs.delete'],
      applications: ['applications.view', 'applications.edit', 'applications.export'],
      gallery: ['gallery.view', 'gallery.edit', 'gallery.create', 'gallery.delete'],
      settings: ['settings.view', 'settings.edit'],
      users: ['users.view', 'users.edit', 'users.create', 'users.delete'],
      audit: ['audit.view']
    };

    if (permissions.includes('all')) return true;
    
    const requiredPerms = sectionPermissions[section] || [];
    return requiredPerms.some(p => hasPermission(p));
  };

  const value = {
    user,
    admin: user, // Backward compatibility
    token,
    login,
    logout,
    loading,
    isAuthenticated: !!token && !!user,
    permissions,
    hasPermission,
    canAccess,
    refreshUser: fetchCurrentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
